import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getModifyParticipantsLimit } from '../../utils/activity';

const ModifyParticipantsDialog = ({
  open,
  onClose,
  onModify,
  currentParticipants,
  maxParticipants,
  currentRegistrations,
  category,
  maxPerBooking,
  error,
}) => {
  const { t } = useTranslation();
  const booked = Number(currentParticipants) > 0 ? Number(currentParticipants) : 1;
  const [newNumberOfParticipants, setNewNumberOfParticipants] = useState(booked);

  useEffect(() => {
    if (open) {
      setNewNumberOfParticipants(booked);
    }
  }, [open, booked]);

  const maxAllowedParticipants = getModifyParticipantsLimit({
    category,
    maxParticipants,
    currentRegistrations,
    currentParticipants: booked,
    maxPerBooking,
  });
  const availableSpots = Math.max(0, (Number(maxParticipants) || 0) - (Number(currentRegistrations) || 0));

  const handleConfirm = () => {
    if (typeof onModify !== 'function') return;
    const next = Math.min(Math.max(1, Number(newNumberOfParticipants) || booked), maxAllowedParticipants);
    onModify(next, true);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          '@media (max-width: 768px)': {
            margin: 2,
            width: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)'
          }
        }
      }}
    >
      <DialogTitle sx={{ 
        fontSize: { xs: '1.25rem', md: '1.5rem' },
        py: { xs: 1.5, md: 2 }
      }}>
        {t('modifyParticipants')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {error && (
            <Typography 
              color="error" 
              variant="body2"
              sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
            >
              {error}
            </Typography>
          )}
          
          <Box>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
            >
              {t('currentParticipants')}: {booked}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
            >
              {t('availableSpots')}: {availableSpots}
            </Typography>
            {booked > 1 && (
              <Typography 
                variant="body2" 
                color="info.main" 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
              >
                {t('modifyParticipantsNote', { count: booked })}
              </Typography>
            )}
          </Box>

          <TextField
            label={t('newNumberOfParticipants')}
            type="number"
            value={newNumberOfParticipants}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (value >= 1 && value <= maxAllowedParticipants) {
                setNewNumberOfParticipants(value);
              }
            }}
            inputProps={{
              min: 1,
              max: maxAllowedParticipants
            }}
            fullWidth
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.875rem', md: '1rem' }
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.875rem', md: '1rem' }
              }
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 2, md: 3 } }}>
        <Button 
          onClick={onClose}
          sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!onModify || newNumberOfParticipants === booked || newNumberOfParticipants > maxAllowedParticipants}
          sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
        >
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModifyParticipantsDialog; 